/**
 * AdminPage — platform-owner oversight (owner-era). READ-ONLY: stat cards, a
 * queue line, and a paginated users table. No action buttons this era.
 *
 * Double-gated: the client only routes here when auth.user.is_owner is true
 * (App.jsx), and every /api/admin/* endpoint independently 403s a non-owner
 * (server truth). A non-owner who forces the URL sees the friendly bounce.
 *
 * Utilitarian per spec §23 — dense, functional, no marketing chrome. The
 * error-tail panel is deferred (no persisted error store exists yet — recorded
 * in amendment v3.22), deliberately not invented here.
 */
import { useCallback, useEffect, useState } from 'react';
import { TopBar } from '../layout/TopBar.jsx';
import { ScrollX } from '../components/ScrollX.jsx';
import { api } from '../api/client.js';
import { formatDateDDMonYYYY } from '../../utils/dates.js';

const TIER_ORDER = ['free', 'starter', 'pro', 'enterprise'];

export function AdminPage({ user, organization, onLogout, navigate }) {
  const isOwner = !!user?.is_owner;
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadError, setLoadError] = useState(null);
  // BACKUP-NET (v3.35): the owner-only Backups card state.
  const [backups, setBackups] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupError, setBackupError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [ov, us] = await Promise.all([api.getAdminOverview(), api.getAdminUsers()]);
      setOverview(ov || null);
      setUsers(Array.isArray(us?.users) ? us.users : []);
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
    }
    // Backups load separately: a 503 (service absent) must not break the page.
    try {
      setBackups(await api.getAdminBackups());
    } catch {
      setBackups(null);
    }
  }, []);

  useEffect(() => { if (isOwner) load(); }, [isOwner, load]);

  const runBackupNow = useCallback(async () => {
    setBackupBusy(true);
    setBackupError(null);
    try {
      await api.runAdminBackup();
      setBackups(await api.getAdminBackups());
    } catch (e) {
      setBackupError(e?.body?.message || 'The backup could not be completed. Check the server log.');
    } finally {
      setBackupBusy(false);
    }
  }, []);

  if (!isOwner) {
    return (
      <div className="app-frame">
        <TopBar user={user} organization={organization} onLogout={onLogout} navigate={navigate} />
        <main className="main-content" role="main">
          <section className="admin-no-access">
            <h1>Restricted</h1>
            <p>This area is available to the platform owner only.</p>
            <button type="button" className="btn-submit" onClick={() => navigate('/dashboard')}>
              Back to dashboard
            </button>
          </section>
        </main>
      </div>
    );
  }

  const q = overview?.queue || {};
  const avg = typeof q.recent_avg_seconds === 'number' ? `${q.recent_avg_seconds.toFixed(1)}s` : '—';

  return (
    <div className="app-frame">
      <TopBar user={user} organization={organization} onLogout={onLogout} navigate={navigate} />
      <main className="main-content admin-page" role="main">
        <section className="admin-hero">
          <h1>Platform oversight</h1>
          <p>Read-only. Live counts across every organisation.</p>
        </section>

        {loadError && (
          <p className="recent-reports-error" role="alert">
            Couldn&rsquo;t load oversight data. Try refreshing.
          </p>
        )}

        <section className="admin-stats" aria-label="Platform totals">
          <StatCard label="Users" value={overview ? overview.users_total : '—'} />
          <StatCard
            label="Organisations"
            value={overview ? overview.orgs_total : '—'}
            sub={overview ? pendingSub(overview.orgs_pending_deletion) : null}
          />
          <StatCard
            label="Reports (all time)"
            value={overview ? overview.reports_all_time : '—'}
            sub={overview ? pendingSub(overview.reports_pending_deletion) : null}
          />
          <StatCard
            label="Reports (last 30 days)"
            value={overview ? overview.reports_last_30_days : '—'}
            sub={overview ? pendingSub(overview.reports_last_30_days_pending) : null}
          />
        </section>

        {overview && (
          <section className="admin-tier-line" aria-label="Organisations by tier">
            {TIER_ORDER.map((t) => {
              const pending = overview.orgs_by_tier_pending?.[t] ?? 0;
              return (
                <span key={t} className="admin-tier-chip">
                  {t}: <strong>{overview.orgs_by_tier?.[t] ?? 0}</strong>
                  {pending > 0 ? ` (${pending} pending deletion)` : ''}
                </span>
              );
            })}
          </section>
        )}

        <section className="admin-queue-line" aria-label="Upload queue">
          <span className="admin-queue-title">Upload queue</span>
          <span>queued: <strong>{overview ? q.queued ?? 0 : '—'}</strong></span>
          <span>in&nbsp;flight: <strong>{overview ? q.in_flight ?? 0 : '—'}</strong></span>
          <span>capacity: <strong>{overview ? (q.depth ?? '—') : '—'}</strong></span>
          <span>recent&nbsp;avg: <strong>{overview ? avg : '—'}</strong></span>
        </section>

        {/* TRUST-PROXY (v3.32) D: how this request resolved — the ten-second
            proof that req.ip is the client (not a Railway POP) and the routing
            tripwire. Owner-only; reflects the owner's current connection. */}
        {overview?.connection && (
          <section className="admin-connection" aria-label="Your connection">
            <span className="admin-connection-title">Your connection</span>
            {[
              ['Resolved IP', overview.connection.resolvedIp],
              ['Socket peer', overview.connection.socketPeer],
              ['X-Forwarded-For', overview.connection.xForwardedFor],
              ['X-Real-IP', overview.connection.xRealIp],
              ['X-Railway-Edge', overview.connection.railwayEdge],
            ].map(([label, value]) => (
              <span key={label} className="admin-connection-row">
                <span className="admin-connection-label">{label}:</span>
                {' '}
                <span className="admin-connection-value">{value || '—'}</span>
              </span>
            ))}
          </section>
        )}

        <section className="admin-users" aria-label="Users">
          <h2>Users</h2>
          {/* MOBILE-FIT M2: the roster slides — closes the real 768 px defect
              (Last upload / Reports columns off-screen). Same columns, same order. */}
          <ScrollX className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Organisation</th>
                  <th scope="col">Tier</th>
                  <th scope="col">Role</th>
                  <th scope="col">Verified</th>
                  <th scope="col">Signed up</th>
                  <th scope="col">Signup IP</th>
                  <th scope="col">Last upload</th>
                  <th scope="col" className="admin-num">Reports</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td colSpan={10} className="admin-empty">No users yet.</td>
                  </tr>
                )}
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.full_name || '—'}</td>
                    <td>{u.email}</td>
                    <td>{u.org_name || '—'}</td>
                    <td>{u.tier}</td>
                    <td>{u.role}</td>
                    <td>{u.verified ? 'Yes' : 'No'}</td>
                    <td>{formatDateDDMonYYYY(u.signup_at)}</td>
                    <td className="admin-signup-ip">{u.signup_ip || '—'}</td>
                    <td>{u.last_upload ? formatDateDDMonYYYY(u.last_upload) : '—'}</td>
                    <td className="admin-num">{u.report_count ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </section>

        {/* BACKUP-NET (v3.35): owner-only Backups card — the nightly online
            snapshot the owner can see and download. Purpose over design. */}
        {backups && (
          <section className="admin-backups" aria-label="Backups">
            <h2>Backups</h2>
            <p className="admin-backups-headline">
              {backups.backups?.length
                ? `Last backup: ${fmtUtc(backups.backups[0].finishedAt || backups.backups[0].mtime)} · ${fmtMB(backups.backups[0].bytes)} · integrity ${backups.backups[0].integrity}`
                : 'No backup yet'}
              {' · '}Next scheduled: {fmtUtc(backups.nextRunAt)}
            </p>
            {/* BACKUP-NET fix-up B: the owner must know the scope limit. */}
            <p className="admin-backups-headline">
              {"Database only — report files under the reports directory are not included, and backups live on this server's volume. Download a copy weekly and keep it off this server."}
            </p>
            <button
              type="button"
              className="btn-submit admin-backup-run"
              disabled={backupBusy || backups.inProgress}
              onClick={runBackupNow}
            >
              {backupBusy || backups.inProgress ? 'Backing up…' : 'Run backup now'}
            </button>
            {backupError && (
              <p className="recent-reports-error" role="alert">{backupError}</p>
            )}
            {backups.backups?.length > 0 && (
              <ScrollX className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Reason</th>
                      <th scope="col">Size</th>
                      <th scope="col">Integrity</th>
                      <th scope="col">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backups.backups.map((b) => (
                      <tr key={b.file}>
                        <td>{fmtUtc(b.finishedAt || b.mtime)}</td>
                        <td>{b.reason || '—'}</td>
                        <td>{fmtMB(b.bytes)}</td>
                        <td>{b.integrity}</td>
                        <td>
                          <a href={`/api/admin/backups/${encodeURIComponent(b.file)}/download`} download={b.file}>
                            Download
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollX>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

// BACKUP-NET (v3.35): "31 Aug 2026, 02:00 UTC" — derived entirely from the ISO
// string's own UTC fields (the shared local-accessor date helper would shift
// the date across midnight on a UTC+3 host, mismatching the UTC clock shown).
const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtUtc(iso) {
  const s = String(iso || '');
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return '—';
  return `${s.slice(8, 10)} ${MONTHS_ABBR[Number(s.slice(5, 7)) - 1]} ${s.slice(0, 4)}, ${s.slice(11, 16)} UTC`;
}

function fmtMB(bytes) {
  if (typeof bytes !== 'number') return '—';
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatCard({ label, value, sub }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-value">{value}</div>
      <div className="admin-stat-label">{label}</div>
      {sub ? <div className="admin-stat-sub">{sub}</div> : null}
    </div>
  );
}

// LAUNCH-OPS B: the pending-deletion sub-label for a tile — shown only when at
// least one org/report is in the grace window (scheduled_deletion_at set).
function pendingSub(n) {
  return n > 0 ? `${n} pending deletion` : null;
}
